import { Box, Button, CircularProgress, Divider, MenuItem, Select, Typography } from '@mui/material'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useAuth } from '../../../../hooks/use-auth'
import { type SystemTimeSpan } from '../../../../models/SystemTimeSpan'
import { GetSystemTimeSpans } from '../../../../services/SystemConfigurationService'
import { ExportRcnEndOfYear } from '../../../../services/ExportService'
import { AuthFunctions, hasPermission } from '../../../../utils/permissions'

export const RcnEndOfYearExport = (): JSX.Element | null => {
  const { user } = useAuth()
  const canExport = user != null &&
    (hasPermission(user, AuthFunctions.ANNUAL_PLANNING_REVIEW) ||
      hasPermission(user, AuthFunctions.ANNUAL_PLANNING_REVIEW_EDIT))

  const [timeSpans, setTimeSpans] = useState<SystemTimeSpan[]>([])
  const [selectedTimeSpanId, setSelectedTimeSpanId] = useState<number | null>(null)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    if (!canExport) return

    const fetchTimeSpans = async (): Promise<void> => {
      const { response, success } = await GetSystemTimeSpans()
      if (!success || response == null) {
        toast.error('Failed to load school years')
        return
      }

      setTimeSpans(response)

      const today = new Date()
      const current = response.find(
        ts => new Date(ts.startDate) <= today && new Date(ts.endDate) >= today
      )
      setSelectedTimeSpanId(current?.id ?? response[0]?.id ?? null)
    }

    void fetchTimeSpans()
  }, [canExport])

  const selectedTimeSpan = timeSpans.find(ts => ts.id === selectedTimeSpanId)

  const handleDownload = async (): Promise<void> => {
    if (selectedTimeSpanId == null) return

    setDownloading(true)
    const { blob, success } = await ExportRcnEndOfYear(selectedTimeSpanId)
    setDownloading(false)

    if (!success || blob == null) {
      toast.error('Failed to generate the export. Please try again.')
      return
    }

    const fileName = `RCN-End-of-Year-${selectedTimeSpan?.name ?? selectedTimeSpanId}.xlsx`
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  if (!canExport) return null

  return <>
    <Divider sx={{ my: '2em' }} />
    <Typography variant='h4'>Exports</Typography>

    <Box sx={{ mt: '2em' }}>
      <Typography variant='h5' sx={{ fontSize: '1em', fontWeight: '600', mb: '.5em' }}>RCN End-of-Year Export</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: '1.5em', maxWidth: '800px' }}>
        Download the statewide RCN End-of-Year workbook (training metrics by RCN and county) for a school year.
      </Typography>

      {timeSpans.length === 0
        ? <CircularProgress size={24} />
        : <Box sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
          <Select
            value={selectedTimeSpanId ?? ''}
            onChange={(e) => { setSelectedTimeSpanId(Number(e.target.value)) }}
            sx={{ fontSize: '.875em', height: '2em' }}
            inputProps={{ 'aria-label': 'School Year' }}
            data-testid='eoyExportYearSelect'
          >
            {timeSpans.map(ts => (
              <MenuItem key={ts.id} value={ts.id}>{ts.name}</MenuItem>
            ))}
          </Select>

          <Button
            variant='contained'
            startIcon={downloading ? <CircularProgress size={16} color='inherit' /> : <DownloadOutlinedIcon />}
            onClick={() => { void handleDownload() }}
            disabled={downloading || selectedTimeSpanId == null}
            data-testid='downloadEoyExport'
          >
            {downloading ? 'Generating…' : 'Download'}
          </Button>
        </Box>
    }
    </Box>
  </>
}
